import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Phone, MessageCircle, Mail, Video, FileText, Sparkles, KeyRound, Shield, Clock, Award, CheckCircle2, AlertTriangle, Star, Search, MapPin, Building2 } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/SiteLayout";
import { buildWaUrl, openWhatsApp, WA_MSG } from "../lib/whatsapp";
import { trackEvent, EVENTS } from "../lib/analytics";
import SEO, { ORG_JSONLD, breadcrumb, faqJsonLd } from "../components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

const HOME_FAQ = [
  { q: "¿Cómo funcionan las subastas inmobiliarias del BOE?", a: "Las subastas públicas inmobiliarias del Boletín Oficial del Estado (BOE) son procedimientos judiciales o administrativos por los que un inmueble se adjudica al mejor postor. Para participar necesitas un depósito previo (habitualmente el 5 %), un certificado digital y conocer las cargas registrales del inmueble antes de pujar. Una nota simple actualizada del Registro de la Propiedad es imprescindible: en ella aparecen hipotecas, embargos, anotaciones y demás cargas que pueden o no cancelarse tras la adjudicación." },
  { q: "¿Qué es la Consultoría de Deudas y qué incluye?", a: "Es un análisis registral profesional de la nota simple del inmueble: detectamos qué cargas se cancelan con la adjudicación, cuáles subsisten y se transmiten al adjudicatario, y posibles riesgos no visibles en el BOE (embargos con mismo rango registral, hipotecas anteriores, deudas de comunidad, ejecuciones cruzadas). Coste 95 € IVA incluido. Entrega en 24-72 horas hábiles." },
  { q: "¿Cuánto cuesta el Informe Premium de Cargas y Deudas?", a: "Desde 135 € IVA incluido cuando se contrata simultáneamente la Gestión Completa Post-Subasta. Como servicio independiente, su tarifa estándar es de 335 € IVA incluido. Incluye análisis registral, valoración de mercado, cálculo del ITP (Impuesto de Transmisiones Patrimoniales), recomendación de puja máxima, acompañamiento telemático el día de la subasta y asesoramiento jurídico en todas las fases posteriores hasta la inscripción registral." },
  { q: "¿Qué es la Gestión Post-Subasta?", a: "Tramitación integral tras la adjudicación: liquidación del ITP, gestión registral, comunicaciones con el juzgado, plusvalía municipal, solicitud de posesión y asistencia jurídica frente a ocupación o terceros. Honorarios del 1 % al 1,5 % sobre el importe de adjudicación, con mínimo de 800 € + IVA." },
  { q: "¿Atendéis subastas en toda España?", a: "Sí. Analizamos subastas inmobiliarias del BOE en todo el territorio nacional: judiciales, AEAT, TGSS y notariales. Trabajamos a distancia desde Marbella (Málaga) mediante videollamada y correo electrónico." },
];

const ORIENTACION_PAYMENT_LINK = "https://buy.stripe.com/8x2aEQ5A02WGeQb4N9gA800";

const SERVICES = [
  {
    icon: Video, slug: "/orientacion", price: "15 €", priceLabel: "/ sesión", tag: "Para empezar",
    title: "Orientación en Subastas",
    subtitle: "¿No sabes ni por dónde empezar?",
    desc: "Videollamada 1 a 1 de 30 min con un experto. Te explicamos cómo funciona el BOE, cargas, depósitos, ITP y resolvemos tus dudas reales.",
    cta: "Reservar videollamada",
  },
  {
    icon: FileText, slug: "/consultoria", price: "95 €", priceLabel: "/ informe", tag: "Para inversores con experiencia",
    title: "Consultoría de Deudas",
    subtitle: "¿Solo quieres despejar dudas sobre la documentación?",
    desc: "Lectura profesional de la nota simple. Te decimos qué cargas se cancelan, cuáles subsisten y si la subasta merece la pena. Sin acompañamiento.",
    cta: "Contratar consultoría",
  },
  {
    icon: Sparkles, slug: "/informe-premium", price: "Desde 135 €", priceLabel: "bonificado con Post-Subasta", tag: "Servicio integral",
    title: "Informe Premium",
    subtitle: "¿Quieres ir acompañado hasta el final?",
    desc: "Informe completo de cargas + valor de mercado + ITP + puja máxima + acompañamiento telemático el día de la subasta. Tu escudo en la puja.",
    cta: "Más información",
  },
  {
    icon: KeyRound, slug: "/gestion-postsubasta", price: "Desde 800 €", priceLabel: "+ IVA · personalizado", tag: "Has ganado, ¿y ahora qué?",
    title: "Gestión Post-Subasta",
    subtitle: "Te guiamos hasta tener la propiedad a tu nombre.",
    desc: "Pago judicial, mandamiento de cancelación, ITP, regularización de deudas no registrales, inscripción registral y dosier completo.",
    cta: "Conocer servicio",
  },
];

const TRUST = [
  { icon: Award, title: "+12 años de experiencia", desc: "Especializados en subastas judiciales y administrativas del BOE." },
  { icon: Shield, title: "Equipo multidisciplinar", desc: "Abogados, procuradores y especialistas fiscales bajo el mismo techo." },
  { icon: Clock, title: "Respuesta ágil", desc: "Análisis registral y conclusiones por escrito en plazos profesionales." },
];

export default function Home() {
  return (
    <div className="min-h-screen bg-stone-50">
      <SEO
        title="Asesoría profesional en subastas BOE en España"
        description="Asesoramiento experto para subastas inmobiliarias del BOE en España: orientación, análisis de cargas y deudas, informes registrales y gestión post-subasta. Evita errores caros antes de pujar."
        keywords="subastas inmobiliarias, subastas BOE, asesoría subastas, informe cargas y deudas, nota simple, ITP subastas, gestión post-subasta, abogado subastas, subastas judiciales"
        path="/"
        jsonLd={[
          ORG_JSONLD,
          breadcrumb([{ name: "Inicio", path: "/" }]),
          faqJsonLd(HOME_FAQ),
        ]}
      />
      <SiteHeader />

      {/* HERO */}
      <section className="bg-[#2d1f1a] text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-[#E0651F]/15 border border-[#E0651F]/40 text-[#E0651F] px-3 py-1 rounded-sm text-[10px] tracking-[0.25em] uppercase font-bold mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Asesoría profesional en Subastas BOE
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] mb-6">
              En subastas, lo que <span className="text-[#E0651F]">no sabes</span> te puede salir caro.
            </h1>
            <p className="text-lg lg:text-xl text-stone-300 leading-relaxed max-w-2xl mb-8 font-display font-medium">
              Te acompañamos desde la <strong className="text-white">orientación inicial</strong> hasta la <strong className="text-white">gestión post-subasta</strong>. Información clara, sin tecnicismos vacíos, para proteger tu inversión.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="#servicios" className="inline-flex items-center gap-2 bg-[#E0651F] hover:bg-white hover:text-[#2d1f1a] text-white px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors" data-testid="hero-cta-services">
                Ver servicios <ArrowRight className="w-4 h-4" />
              </a>
              <a href={buildWaUrl(WA_MSG.generic)} target="_blank" rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); trackEvent(EVENTS.CTA_WHATSAPP, { source: "home_hero" }); openWhatsApp(WA_MSG.generic); }}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white hover:text-[#2d1f1a] text-white border border-white/30 px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
                data-testid="hero-cta-whatsapp">
                <MessageCircle className="w-4 h-4" /> Hablar con un asesor
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-xs text-stone-400">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#E0651F]" /> +12 años de experiencia</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#E0651F]" /> Equipo jurídico y fiscal</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#E0651F]" /> Pagos seguros con Stripe</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white text-[#2d1f1a] p-7 rounded-sm border-l-4 border-[#E0651F] shadow-2xl">
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">¿Por dónde empiezo?</div>
              <h3 className="font-display text-2xl font-black tracking-tight mb-4 leading-tight">Elige según tu momento.</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex gap-3"><span className="font-mono text-xs text-[#E0651F] mt-0.5 w-6 flex-shrink-0">01</span><span><strong>"No sé nada de subastas"</strong> → <Link to="/orientacion" className="text-[#E0651F] font-bold">Orientación 15 €</Link></span></li>
                <li className="flex gap-3"><span className="font-mono text-xs text-[#E0651F] mt-0.5 w-6 flex-shrink-0">02</span><span><strong>"Tengo dudas sobre una nota simple"</strong> → <Link to="/consultoria" className="text-[#E0651F] font-bold">Consultoría 95 €</Link></span></li>
                <li className="flex gap-3"><span className="font-mono text-xs text-[#E0651F] mt-0.5 w-6 flex-shrink-0">03</span><span><strong>"Quiero ir acompañado hasta la puja"</strong> → <Link to="/informe-premium" className="text-[#E0651F] font-bold">Informe Premium</Link></span></li>
                <li className="flex gap-3"><span className="font-mono text-xs text-[#E0651F] mt-0.5 w-6 flex-shrink-0">04</span><span><strong>"Ya he ganado, ¿y ahora qué?"</strong> → <Link to="/gestion-postsubasta" className="text-[#E0651F] font-bold">Gestión Post-Subasta</Link></span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* BUSCADOR BOE — bloque destacado */}
      <SubastasSearchBlock />


      {/* PAIN POINTS */}
      <section className="bg-stone-100 py-20">
        <div className="max-w-5xl mx-auto px-6 text-center mb-12">
          <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">El problema</div>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#2d1f1a] mb-5 leading-tight">
            Una subasta puede ser una oportunidad… o un quebradero de cabeza judicial.
          </h2>
          <p className="text-base text-stone-600 leading-relaxed max-w-3xl mx-auto">
            Mala interpretación de una carga · omisión del ITP · ocupación no detectada · cargas posteriores que sí afectan · plazos para el pago judicial que se incumplen. Cualquier descuido convierte una buena adjudicación en una pérdida económica importante.
          </p>
        </div>
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-3 gap-5">
          {[
            "❓ No entiendes la nota simple ni qué cargas se cancelan.",
            "💸 Pujas sin saber el ITP real que pagarás después.",
            "🏚️ Ganas y descubres que el inmueble está ocupado.",
            "📑 No sabes qué hacer tras el decreto de adjudicación.",
            "⚖️ Plazos judiciales que se te escapan y generan recargos.",
            "🤔 Crees que vas bien… hasta que aparece una carga oculta.",
          ].map((p, i) => (
            <div key={i} className="bg-white border-l-2 border-[#E0651F] p-5 text-sm text-stone-700 leading-relaxed">{p}</div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="servicios" className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-14">
          <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">La solución</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#2d1f1a] mb-4 leading-tight">
            4 servicios para cubrirte en cada fase.
          </h2>
          <p className="text-base text-stone-600 leading-relaxed">
            Desde la primera duda hasta la inscripción registral. Tú eliges hasta dónde quieres delegar.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Link key={s.slug} to={s.slug} className="group block bg-white border border-stone-200 hover:border-[#E0651F] rounded-sm p-7 transition-colors" data-testid={`service-card-${s.slug.replace('/','')}`}>
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div className="w-12 h-12 bg-[#2d1f1a] group-hover:bg-[#E0651F] flex items-center justify-center rounded-sm transition-colors">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-stone-500 mb-1">{s.tag}</div>
                    <div className="font-display text-2xl font-black tracking-tight text-[#2d1f1a]">{s.price}</div>
                    <div className="text-[10px] text-stone-500">{s.priceLabel}</div>
                  </div>
                </div>
                <div className="font-mono text-xs text-[#E0651F] mb-2">{String(i + 1).padStart(2, "0")} · {s.subtitle}</div>
                <h3 className="font-display text-xl font-black tracking-tight text-[#2d1f1a] mb-3 leading-tight">{s.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed mb-5">{s.desc}</p>
                <span className="inline-flex items-center gap-1.5 text-xs tracking-[0.18em] uppercase font-bold text-[#E0651F] group-hover:gap-3 transition-all">
                  {s.cta} <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-[#2d1f1a] text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-12">
            <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">Por qué confiar</div>
            <h2 className="font-display text-4xl font-black tracking-tight leading-tight">
              Más de una década protegiendo inversores en subastas.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {TRUST.map((t, i) => {
              const Icon = t.icon;
              return (
                <div key={i} className="border-l-2 border-[#E0651F] pl-5">
                  <Icon className="w-7 h-7 text-[#E0651F] mb-3" />
                  <h3 className="font-display font-black text-xl tracking-tight mb-2">{t.title}</h3>
                  <p className="text-sm text-stone-400 leading-relaxed">{t.desc}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-14">
            <TestimonialsBlock />
          </div>
        </div>
      </section>

      {/* FAQ — SEO + UX */}
      <section className="bg-white py-20 border-t border-stone-200" aria-labelledby="faq-heading">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">Preguntas frecuentes</div>
          <h2 id="faq-heading" className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#2d1f1a] mb-10 leading-tight">
            Lo que más nos preguntan sobre subastas BOE.
          </h2>
          <div className="space-y-3">
            {HOME_FAQ.map((it, i) => (
              <details key={i} className="group bg-stone-50 border border-stone-200 rounded-sm p-5 hover:border-[#E0651F] transition-colors" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <h3 className="font-display font-black text-base sm:text-lg text-[#2d1f1a] leading-snug">{it.q}</h3>
                  <span className="font-mono text-2xl text-[#E0651F] leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-stone-700 leading-relaxed">{it.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <AlertTriangle className="w-10 h-10 text-[#E0651F] mx-auto mb-6" />
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#2d1f1a] mb-5 leading-tight">
            En subastas, la información es poder.
          </h2>
          <p className="text-base text-stone-600 mb-8 max-w-xl mx-auto leading-relaxed">
            Conocer las reglas del juego es la mejor jugada. Reserva una videollamada de orientación o cuéntanos tu caso por WhatsApp.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href={ORIENTACION_PAYMENT_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#2d1f1a] hover:bg-[#E0651F] text-white px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors">
              Reservar Orientación · 15 € <ArrowRight className="w-4 h-4" />
            </a>
            <a href={buildWaUrl(WA_MSG.generic)} target="_blank" rel="noopener noreferrer"
              onClick={(e) => { e.preventDefault(); trackEvent(EVENTS.CTA_WHATSAPP, { source: "home_final" }); openWhatsApp(WA_MSG.generic); }}
              className="inline-flex items-center gap-2 bg-white hover:bg-[#2d1f1a] hover:text-white text-[#2d1f1a] border border-stone-300 px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
              data-testid="final-cta-whatsapp">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

// ---- Testimonios reales (Google Reviews) ----
const TESTIMONIALS = [
  {
    initial: "E",
    name: "Eloy Valle",
    stars: 5,
    when: "Hace 10 meses",
    text: "Servicio de 10 de Fernando. Te explica cada detalle de tu caso y te recomienda pasos a seguir. Muy profesionales. Sin duda volvería a contratar sus servicios.",
    badgeColor: "bg-emerald-600",
  },
  {
    initial: "S",
    name: "Sergio Andreo",
    badge: "Local Guide · 28 reseñas",
    stars: 5,
    when: "Hace un año",
    text: "Ayuda imprescindible para poder participar en cualquier subasta con garantía de éxito. Especial mención a Fernando por su atención y profesionalidad. De 10!!",
    badgeColor: "bg-orange-500",
  },
  {
    initial: "M",
    name: "Marta Polo",
    badge: "Local Guide · 32 reseñas",
    stars: 5,
    when: "Hace un año",
    text: "Extraordinaria atención. Me he sentido muy informada y acompañada en el proceso. Gracias Fernando, un trato extraordinario y muy serio. Te lo explican todo muy sencillo y da mucha seguridad. Lo recomiendo 100 %.",
    badgeColor: "bg-amber-700",
  },
  {
    initial: "S",
    name: "Silvia P.",
    badge: "6 reseñas",
    stars: 5,
    when: "Hace un año",
    text: "Le pongo cinco estrellas porque no hay más, por su gran profesionalidad. No solo me realizaron un informe de cargas súper completo por videoconferencia, sino que aparte de aconsejarme me contestaron a varias consultas, respondiendo a todas mis dudas. Recomendables 100 %.",
    badgeColor: "bg-purple-600",
  },
];

const GOOGLE_REVIEWS_URL = "https://www.google.com/search?sca_esv=4533f9585008942f&hl=es&authuser=3&uds=ALYpb_ncDc7jTlmw6Mmq7NjuX5c-uTFzxTebdSqcyFMHbG8eqyFnvDEcidE9J1139qKpY3XLoAGBwJeSCskUJOti2au2uodJ6-N95rLIw3SKCB8qQA-JkUlA34MiK_BmgsX8lyul7aiJugqjYKd5WLhspxfn9SUhm2pqXvd9EaniC1d99NbXDKofds98e-R98myWdOqLGsf0vQRBhoK7eXDmwqmHdVBYKNmUR-VuqsATIwI2lDkkHDCezAkFSGEiSRWR4xJ_NX7HJVIGfSfD53MSVTAeXyI_KFVygdCUYLOUFngMeVcfalJ-4dI8kL4ziZhfy41GEh2lcpu7vLss4aUnVQ1DGeAk9g&q=FM+Subastas+%26+Investment+Rese%C3%B1as&si=AL3DRZEsmMGCryMMFSHJ3StBhOdZ2-6yYkXd_doETEE1OR-qOfqMZq78lC7jPBi26Fu17BRO67quocEknhV8lLZqr1w3owuf6u-eZblgOn1lAAavQ5j40StSO6aSkwldDt-2_eJYAoal9HzBSEmWjjpPSE3Ywa9noA%3D%3D&sa=X&ved=2ahUKEwip37ebg8-UAxW1caQEHWFxAmgQ_4MLegQIJRAO";

function TestimonialsBlock() {
  return (
    <div>
      <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">Lo que dicen mis clientes</div>
      <h3 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-white mb-10 leading-tight max-w-2xl">
        5 estrellas en Google de clientes reales.
      </h3>
      <div className="grid sm:grid-cols-2 gap-5">
        {TESTIMONIALS.map((t, i) => (
          <div key={i} className="bg-white/5 border border-stone-700/40 hover:border-[#E0651F]/60 p-6 rounded-sm transition-colors" data-testid={`testimonial-${i}`}>
            <div className="flex items-start gap-3 mb-3">
              <div className={`w-11 h-11 ${t.badgeColor} text-white font-display font-black text-lg flex items-center justify-center rounded-full flex-shrink-0`}>{t.initial}</div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-white">{t.name}</div>
                {t.badge && <div className="text-[11px] text-stone-500">{t.badge}</div>}
                <div className="flex items-center gap-1.5 mt-1">
                  {Array.from({ length: t.stars }).map((_, k) => (
                    <Star key={k} className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                  ))}
                  <span className="text-[11px] text-stone-500 ml-1">· {t.when}</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-stone-300 leading-relaxed">"{t.text}"</p>
          </div>
        ))}
      </div>
      <div className="mt-8 text-center">
        <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer"
          data-testid="testimonials-google-link"
          className="inline-flex items-center gap-2 text-stone-300 hover:text-[#E0651F] text-xs uppercase tracking-[0.2em] font-bold transition-colors">
          Ver todas las reseñas en Google <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}

function SubastasSearchBlock() {
  const navigate = useNavigate();
  const [provincias, setProvincias] = useState({});
  const [stats, setStats] = useState(null);
  const [provincia, setProvincia] = useState("");
  const [localidad, setLocalidad] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/api/subastas?per_page=1`),
      axios.get(`${API}/api/subastas/stats`),
    ]).then(([a, b]) => {
      if (cancelled) return;
      setProvincias(a.data.provincias || {});
      setStats(b.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const qs = new URLSearchParams();
    if (provincia) qs.set("provincia", provincia);
    if (localidad.trim()) qs.set("localidad", localidad.trim());
    navigate(`/subastas${qs.toString() ? "?" + qs.toString() : ""}`);
  };

  const totalActivas = stats?.total_activas || 0;
  const top = stats?.top_provincias || [];

  return (
    <section
      className="relative bg-[#1a110e] text-white py-16 lg:py-20 overflow-hidden border-y border-[#3d2820]"
      data-testid="home-buscador-block"
    >
      {/* Pattern decorativo */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" aria-hidden="true">
        <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-[#E0651F] blur-[120px]" />
        <div className="absolute -bottom-20 -left-20 w-[300px] h-[300px] rounded-full bg-[#E0651F] blur-[100px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16 items-center">
          {/* IZQUIERDA: Pitch + stats */}
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-3 flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" /> Buscador en vivo
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] mb-5">
              {totalActivas > 0 ? (
                <>
                  <span className="text-[#E0651F]">{totalActivas.toLocaleString("es-ES")}</span> subastas activas
                  <br />
                  <span className="text-stone-300 text-2xl sm:text-3xl lg:text-4xl font-normal">esperando ser analizadas.</span>
                </>
              ) : (
                <>Explora todas las <span className="text-[#E0651F]">subastas BOE</span> en activo.</>
              )}
            </h2>
            <p className="text-stone-400 text-sm sm:text-base leading-relaxed max-w-md mb-6">
              Datos oficiales del Portal del BOE. Filtra por <strong className="text-white">provincia, municipio</strong>, tipo de inmueble y origen.
              Sincronización automática los lunes y jueves.
            </p>

            {/* Top provincias chips */}
            {top.length > 0 && (
              <div className="hidden sm:block">
                <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mb-2.5">Top provincias activas</div>
                <div className="flex flex-wrap gap-1.5">
                  {top.slice(0, 6).map((p) => (
                    <Link
                      key={p.codigo}
                      to={`/subastas?provincia=${p.codigo}`}
                      className="inline-flex items-center gap-1.5 bg-stone-800/70 hover:bg-[#E0651F]/20 border border-stone-700 hover:border-[#E0651F]/60 px-3 py-1.5 rounded-sm text-xs transition-colors"
                      data-testid={`home-top-provincia-${p.codigo}`}
                    >
                      {p.nombre}
                      <span className="text-stone-400 text-[10px]">{p.n}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* DERECHA: Formulario buscador */}
          <form
            onSubmit={submit}
            className="bg-stone-900/80 backdrop-blur border border-stone-800 rounded-sm p-6 sm:p-8 shadow-2xl"
            data-testid="home-buscador-form"
          >
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-4">Encuentra tu subasta</div>

            <div className="space-y-4">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1.5 block">Provincia</label>
                <select
                  value={provincia}
                  onChange={(e) => setProvincia(e.target.value)}
                  className="w-full bg-stone-950 border border-stone-700 rounded-sm px-3 py-3 text-sm focus:border-[#E0651F] focus:outline-none text-white"
                  data-testid="home-buscador-provincia"
                >
                  <option value="">Todas las provincias</option>
                  {Object.entries(provincias).sort((a, b) => a[1].localeCompare(b[1])).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1.5 block">Localidad / Municipio</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                  <input
                    type="search"
                    value={localidad}
                    onChange={(e) => setLocalidad(e.target.value)}
                    placeholder="Marbella, Arganda, Cambre…"
                    className="w-full bg-stone-950 border border-stone-700 rounded-sm pl-9 pr-3 py-3 text-sm placeholder:text-stone-600 focus:border-[#E0651F] focus:outline-none text-white"
                    data-testid="home-buscador-localidad"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="w-full mt-6 bg-[#E0651F] hover:bg-white hover:text-[#1a110e] text-white py-3.5 text-xs tracking-[0.25em] uppercase font-bold rounded-sm transition-colors flex items-center justify-center gap-2"
              data-testid="home-buscador-submit"
            >
              <Search className="w-4 h-4" /> Buscar subastas
            </button>

            <Link
              to="/subastas"
              className="block text-center mt-3 text-xs tracking-[0.2em] uppercase text-stone-400 hover:text-[#E0651F] transition-colors"
              data-testid="home-buscador-ver-todas"
            >
              o ver el listado completo <ArrowRight className="w-3 h-3 inline -mt-0.5" />
            </Link>
          </form>
        </div>
      </div>
    </section>
  );
}

